/**
 * Vista "Hoy" y cálculo del próximo disparo de un recordatorio.
 */
import { addDays, isoWeekday, type LocalDate } from './dates.js';
import { activeEnd, evaluateHabit, generateUEs, taskStatus, type TaskStatus } from './evaluate.js';
import { logicalDateFor, parseTimeOfDay, wallClock, zonedTimeToInstant } from './time.js';
import type { EngineSettings, EvaluationInput, HabitDef, MoveLike, UEResult } from './types.js';

export interface TodayItem {
  habitId: string;
  /** UE que contiene hoy, o null si hoy no está programado. */
  ue: UEResult | null;
  task: TaskStatus | null;
}

/** Devuelve el estado de hoy para un hábito (null si no corresponde mostrarlo). */
export function todayItem(input: EvaluationInput): TodayItem | null {
  const { habit, today } = input;
  if (habit.kind === 'task') {
    const status = taskStatus(habit, today);
    if (status === 'completed' && habit.completedAt && habit.completedAt.slice(0, 10) < today) return null;
    return { habitId: habit.id, ue: null, task: status };
  }
  if (today < habit.startDate || today > activeEnd(habit)) return null;
  const results = evaluateHabit(input, { from: today, to: today });
  const ue = results[0] ?? null;
  if (!ue) return null;
  return { habitId: habit.id, ue, task: null };
}

export interface ReminderSpec {
  time: string;
  /** Días ISO permitidos; null o vacío = días programados del hábito. */
  days?: number[] | null;
}

/**
 * Próximo instante (ms UTC) estrictamente posterior a `afterMs` en que debe
 * dispararse el recordatorio, o null si no hay ninguno en los próximos 400 días.
 */
export function nextReminderInstant(
  habit: HabitDef,
  settings: EngineSettings,
  reminder: ReminderSpec,
  tz: string,
  dayStartMinutes: number,
  afterMs: number,
  moves?: MoveLike[],
): number | null {
  const minutes = parseTimeOfDay(reminder.time);
  let d = wallClock(afterMs, tz).date;
  for (let i = 0; i < 400; i++, d = addDays(d, 1)) {
    const t = zonedTimeToInstant(d, minutes, tz);
    if (t <= afterMs) continue;
    const logical: LocalDate = logicalDateFor(t, tz, dayStartMinutes);
    if (habit.kind === 'task') {
      if (habit.completedAt) return null;
      // Con fecha, solo se avisa ese día (fecha de calendario de la hora elegida);
      // pasada la fecha no se insiste. Sin fecha, aviso diario hasta completarla.
      if (habit.dueDate) {
        if (d < habit.dueDate) continue;
        if (d > habit.dueDate) return null;
      }
      return t;
    }
    if (logical < habit.startDate) continue;
    if (logical > activeEnd(habit)) return null;
    if (reminder.days && reminder.days.length > 0) {
      if (reminder.days.includes(isoWeekday(d))) return t;
      continue;
    }
    if (generateUEs(habit, settings, logical, logical, moves).length > 0) return t;
  }
  return null;
}
